"use client";

import React from "react";
import { MapPin, Search, TrendingUp, Target, Users, CircleCheck as CheckCircle2, ArrowRight, Compass, ChartBar as BarChart3 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
export default function Hull() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: custom * 0.1,
        ease: "easeOut"
      }
    })
  };
  const landscapeStats = [{
    id: "stat-1",
    number: "76%",
    label: "Local Intent",
    description: "Of commercial searches in Hull carry explicit local intent (e.g., 'near me' or 'in Hull').",
    icon: MapPin
  }, {
    id: "stat-2",
    number: "3.2x",
    label: "Higher Conversion",
    description: "Local Map Pack leads convert 3.2 times faster than generic directory traffic.",
    icon: Target
  }, {
    id: "stat-3",
    number: "64%",
    label: "Market Gap",
    description: "Of local Hull businesses rely on outdated agency tactics that fail modern search standards.",
    icon: TrendingUp
  }];
  const searchBehaviors = [{
    id: "behavior-1",
    title: "Mobile-First Local Discovery",
    description: "Over 68% of local service queries in Hull originate from mobile devices while users are actively looking for immediate solutions.",
    highlight: "High Urgency Conversion",
    icon: Search
  }, {
    id: "behavior-2",
    title: "Google Map Pack Preference",
    description: "Customers in Hull trust Google Business Profiles with verified local reviews over traditional organic listings or paid ads.",
    highlight: "Trust-Driven Selection",
    icon: Users
  }, {
    id: "behavior-3",
    title: "Specific Service Keywords",
    description: "Searchers use precise long-tail queries combining service terms with specific Hull areas like Hessle, Kingswood, or Beverley.",
    highlight: "Hyper-Targeted Intent",
    icon: Compass
  }];
  const strategicPillars = ["Comprehensive Hull & East Yorkshire Technical SEO Audit", "Google Business Profile Optimization & Local Citation Sync", "Location-Specific Content Tailored to Local Search Behavior", "Transparent Keyword Tracking with Direct Access to Me"];
  return <section id="hulllocalsearchlandscape" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <motion.div className="max-w-3xl mx-auto text-center space-y-4" initial="hidden" whileInView="visible" viewport={{
        once: true
      }} custom={0} variants={fadeInVariants}>
          <div className="flex items-center justify-center gap-2">
            <Badge variant="outline" className="border-accent text-accent-foreground bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
              Regional Market Insights
            </Badge>
          </div>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-primary tracking-tight">
            Hull Local Search Landscape
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When I evaluate search demand across Hull and East Yorkshire, I see an incredible opportunity for businesses to outrank competitors who rely on generic, cookie-cutter agency packages.
          </p>
        </motion.div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12 md:my-16">
          {landscapeStats.map((stat, i) => {
          const IconComponent = stat.icon;
          return <motion.div key={stat.id} data-index={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} custom={i + 1} variants={fadeInVariants}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl sm:text-4xl font-extrabold text-primary font-serif">
                        {stat.number}
                      </span>
                      <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground">
                        <IconComponent className="w-5 h-5" />
                      </div>
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {stat.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {stat.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center my-12">
          {}
          <motion.div className="lg:col-span-7 space-y-6" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} custom={1} variants={fadeInVariants}>
            <div className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent font-mono">
                Customer Search Behavior
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-primary font-serif">
                How Customers Find Local Services in Hull
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Understanding local buyer psychology in Hull is central to my SEO strategy. Rather than targeting broad UK-wide keywords with fierce competition, I focus on capturing high-intent search traffic right at the point of decision.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {searchBehaviors.map((behavior, i) => {
              const BehaviorIcon = behavior.icon;
              return <div key={behavior.id} data-index={i} className="p-4 rounded-xl border border-border bg-card flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-md bg-accent/15 text-accent-foreground shrink-0 mt-0.5 sm:mt-0">
                        <BehaviorIcon className="w-5 h-5" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-semibold text-sm sm:text-base text-foreground">
                          {behavior.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                          {behavior.description}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                      {behavior.highlight}
                    </Badge>
                  </div>;
            })}
            </div>
          </motion.div>

          {}
          <motion.div className="lg:col-span-5" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} custom={2} variants={fadeInVariants}>
            <Card className="border border-border bg-card shadow-md relative overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-primary via-accent to-primary" />
              <CardHeader className="space-y-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary font-mono">
                    Direct Consultant Advantage
                  </span>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold text-primary font-serif">
                  My Tailored Local Strategy for Hull Businesses
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  You work directly with me, Yassine Essaadi. No account managers, no junior staff passing around your campaign.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {strategicPillars.map((pillar, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground leading-snug">
                        {pillar}
                      </span>
                    </div>)}
                </div>

                <div className="p-4 rounded-lg bg-secondary/60 border border-border space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-foreground">
                    Market Opportunity
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    By combining localized technical SEO with structured schema markup and targeted content, I help your business secure top visibility in both Google Maps and organic search rankings across Hull.
                  </p>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <Button asChild variant="default" size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      Book a Free Consultation
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {}
        <motion.div className="mt-16 p-8 sm:p-10 rounded-2xl bg-card border border-border shadow-sm text-center max-w-4xl mx-auto space-y-6" initial="hidden" whileInView="visible" viewport={{
        once: true
      }} custom={3} variants={fadeInVariants}>
          <div className="space-y-2">
            <h3 className="text-2xl sm:text-3xl font-bold text-primary font-serif">
              Ready to Dominate Hull Local Search?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Let me analyze your current local search visibility in Hull and map out a bespoke strategy for sustainable organic growth.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Button asChild variant="default" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                Get Your Free Local SEO Strategy
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent hover:text-accent-foreground">
              <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}